/**
 * Fila del Action Center Premium (0668+).
 *
 * Grid 6 columnas alineadas con el header de ActionCenterTable:
 *   TIPO (72px) · ASUNTO (1fr min 280) · CÓDIGO (140) · SOLICITANTE (200) · FECHA (110) · ESTADO (120)
 *
 * Click → abre modal centrado (no panel deslizable). Hover sutil.
 */

import type { ActionItem, ActionItemTipo } from '../hooks/useActionCenterList';

const TIPO_BADGE: Record<ActionItemTipo, { label: string; bg: string; fg: string }> = {
  ORGANIGRAMA: { label: 'ORG', bg: '#E0F2FE', fg: '#075985' },
  DOC: { label: 'DOC', bg: '#FCE7F3', fg: '#9D174D' },
  FLUJOGRAMA: { label: 'FLW', bg: '#FEF3C7', fg: '#92400E' },
  APROBACION_DOCUMENTO: { label: 'DOC*', bg: '#E5E7EB', fg: '#374151' },
  APROBACION_PETICION: { label: 'PET', bg: '#D1FAE5', fg: '#065F46' },
  APROBACION_COMPRA: { label: 'COMP', bg: '#FEE2E2', fg: '#991B1B' },
};

export const ROW_GRID_COLS = '72px minmax(280px, 1fr) 140px 200px 110px 120px';

export interface ActionCenterRowProps {
  item: ActionItem;
  isSelected: boolean;
  onSelect: (item: ActionItem) => void;
  formatDate: (iso: string) => string;
}

export function ActionCenterRow(props: ActionCenterRowProps) {
  const { item, isSelected, onSelect, formatDate } = props;
  const badge = TIPO_BADGE[item.tipo];

  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      style={{
        display: 'grid',
        gridTemplateColumns: ROW_GRID_COLS,
        gap: 16,
        alignItems: 'center',
        width: '100%',
        padding: '0 20px',
        height: 44,
        background: isSelected ? '#F1F5FB' : 'transparent',
        borderLeft: isSelected ? '3px solid #1F3864' : '3px solid transparent',
        borderBottom: '1px solid #F3F4F6',
        textAlign: 'left',
        cursor: 'pointer',
        fontFamily: 'DM Sans, sans-serif',
        transition: 'background 120ms ease',
      }}
      onMouseEnter={(e) => {
        if (!isSelected) e.currentTarget.style.background = '#FAFBFD';
      }}
      onMouseLeave={(e) => {
        if (!isSelected) e.currentTarget.style.background = 'transparent';
      }}
    >
      {/* Col 1 — Tipo (badge) */}
      <span
        style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: 11,
          fontWeight: 700,
          padding: '3px 8px',
          background: badge.bg,
          color: badge.fg,
          borderRadius: 4,
          textAlign: 'center',
          letterSpacing: 0.5,
          justifySelf: 'start',
        }}
      >
        {badge.label}
      </span>

      {/* Col 2 — Asunto (bold) */}
      <span
        style={{
          fontSize: 13,
          fontWeight: item.asunto ? 600 : 500,
          color: '#1F2937',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={item.asunto ?? item.titulo}
      >
        {item.asunto ?? item.titulo}
      </span>

      {/* Col 3 — Código */}
      <span
        style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: 11,
          color: '#9CA3AF',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {item.codigo ?? '—'}
      </span>

      {/* Col 4 — Solicitante */}
      <span
        style={{
          fontSize: 12,
          color: '#4B5563',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {item.solicitante.nombre ?? '—'}
      </span>

      {/* Col 5 — Fecha */}
      <span
        style={{
          fontSize: 12,
          color: '#6B7280',
          fontFamily: 'JetBrains Mono, monospace',
        }}
      >
        {formatDate(item.fechaSolicitud)}
      </span>

      {/* Col 6 — Estado (chip) */}
      <span
        style={{
          fontSize: 10,
          fontWeight: 600,
          color: '#374151',
          padding: '3px 8px',
          background: '#F3F4F6',
          border: '1px solid #E5E7EB',
          borderRadius: 12,
          textAlign: 'center',
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          justifySelf: 'start',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
        }}
      >
        {item.estado.replace(/_/g, ' ')}
      </span>
    </button>
  );
}
